import WatchGrid from "components/watch-grid/WatchGrid"
import { WatchgridProvider } from "components/watch-grid/context"
import { allParams } from "components/watch-grid/utils"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { usePage } from "contexts/Page"
import { useSuspender } from "contexts/Suspender"
import { useUser } from "contexts/User"
import { Helmet } from "react-helmet-async"
import GroupedGrid from "components/grouped-variations/WatchGrid"
import { writeSS, readSS } from "components/grouped-variations/context"
import { useEffect, useState, useRef } from "react"
import useSSR from "hooks/useSSR"
import PageTitleBlock from "components/text/PageTitleBlock"
import styled from "@emotion/styled"
import { body50, bold, legend100, legend80 } from "css/text"
import { getCurrentUrl } from "./../watch-grid/utils"
import getMediaQuery from "css/breakpoints"
import { useLocation, useParams } from "react-router-dom"

const Root = styled.div`
  --subnav-ofs: calc(var(--subnav-height) * var(--is-sub));

  margin-block-end: 100px;

  .page-title-block {
    position: unset;
    color: inherit;
    margin-block: calc(10vh + var(--subnav-ofs)) 50px;

    & > * {
      grid-column: 1/-1;
    }
  }
`

const Wrapper = styled.div`
  display: grid;
  justify-content: center;
  margin-block: -20px 0;
`

const List = styled.ul`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-block-end: 10px;
  width: var(--watches-grid-width);
  max-width: 500px;
  align-items: center;

  ${getMediaQuery("m")} {
    width: 50vw;
  }
`

const Item = styled.li`
  //  flex-shrink: 1;
  flex: 1;
  position: relative;
  text-align: center;
`

const Label = styled.label`
  display: block;
  font-size: 16px;
  padding-inline: 20px;
  padding-block: 10px;
  ${bold}
  cursor: pointer;
  user-select: none;
  transition: color 0.3s;
  overflow: hidden;
  border-bottom: 2px solid transparent;

  &:hover {
    color: rgb(var(--green));
  }
`

const Input = styled.input`
  position: absolute;
  top: -10px;
  left: -10px;
  opacity: 0;

  &:checked + label {
    border-color: rgb(var(--green));
    color: rgb(var(--green));
  }

  &.focus-visible + label {
    outline: 2px solid rgb(var(--focus, 0 255 255) / 1);
    outline-offset: -2px;
  }
`

export default function BootstrapWatches(props) {
  const console = useConsole()
  const locale = useLocale()
  const navigation = useNavigation()
  const location = useLocation()
  const dictionary = useDictionary()
  const ssr = useSSR()
  const { grid } = useParams()
  const page = usePage()
  const env = useEnv()
  const { countryCode } = useUser()
  const numberOfResults = 72
  const firstResult = 0

  const params = new URLSearchParams(location.search)
  const items = [
    {
      id: "f_grid",
      localizedValue: dictionary["watchgridTitleFull"](),
    },
    {
      id: "g_grid",
      localizedValue: dictionary["watchgridTitleGroup"](),
    },
  ]
  const [selected, setSelected] = useState(null)
  const gParams = useRef(["", "group=1"])
  const currentUrl = getCurrentUrl()

  const watchgridURL = new URL(`/api/catalog/watchgrid?language=${locale.current.codes.www}`, process.browser ? env.services : process.env.SERVICE_CATALOG_URL)
  const facet = grid ?? "all"
  watchgridURL.searchParams.set("firstResult", firstResult)
  watchgridURL.searchParams.set("numberOfResults", numberOfResults)
  watchgridURL.searchParams.set("grid", facet)
  watchgridURL.searchParams.set("countryCode", countryCode)
  // get visitorId from cookie
  let visitorId
  if (process.browser && document?.cookie) {
    visitorId = document.cookie
      .split("; ")
      .find(row => row.startsWith("coveo_visitorId"))
      ?.split("=")[1]
    if (visitorId) {
      watchgridURL.searchParams.set("visitorId", visitorId)
      watchgridURL.searchParams.set("clientId", visitorId)
    }
  }

  const cloneWatchgridURL = watchgridURL

  if (params.toString()) {
    params.forEach((value, key) => {
      if (key === "sort") watchgridURL.searchParams.set("sort", value)

      if (allParams.includes(key)) {
        const allValues = params.getAll(key)

        if (allValues.length === 1) watchgridURL.searchParams.set(key, value)
        else watchgridURL.searchParams.set(key, `(${allValues.toString()})`)
      }
    })
  }

  const words = facet.split("-")

  let dicoKey = ""
  words.map((w, i) => {
    if (i === 0) {
      dicoKey += w
    } else {
      dicoKey += w.charAt(0).toUpperCase() + w.slice(1)
    }
  })

  const [[err, gridData], gridSSR] = useSuspender(
    ["grid", locale.current.codes.www, facet, firstResult, watchgridURL],
    () =>
      Promise.all([
        Promise.resolve(watchgridURL)
          .then(url => new Request(url))
          .then(request => fetch(request))
          .then(response => response.json())
          .then(data => {
            const { totalCount, results } = data
            if (totalCount >= 1) return data
            if (totalCount === 0) throw { status: 404 }
            throw { status: 404 }
          }),
      ])
        .then(([data]) => {
          const title = page.meta.title.replaceAll("%name%", dictionary[dicoKey]())
          const description = page.meta.description.replaceAll("%name%", dictionary[dicoKey]())
          const ogtitle = page.meta.title_og.replaceAll("%name%", dictionary[dicoKey]())
          const ogdescription = page.meta.description_og.replaceAll("%name%", dictionary[dicoKey]())
          return [null, Object.assign(data, { title, description, ogtitle, ogdescription })]
        })
        .catch(err => {
          console.error(err)
          return [err, null]
        }),
    true
  )

  if (err)
    if (!process.browser) throw err
    else {
      console.error("err.status: ", err.status, err.description)
      if (err.status === 301) navigation.push(navigation.localize(err.location))
      else navigation.push("/404")
    }

  //  console.verbose("BootstrapWatches(%o)", gridData)
  /*
    let waUpdate = useRef(false)
    let waUpdateTm = useRef(0).current
  
    function updateWA() {
      clearTimeout(waUpdateTm)
      if (!waUpdate) return
      waUpdateTm = setTimeout(update, 200)
  
      function update() {
        let [lastpageload] = window.digitalDataLayer?.filter(o => o.event === "Page Load")?.reverse()
        if (!lastpageload) return
        lastpageload = JSON.parse(JSON.stringify(lastpageload))
        lastpageload.data.page.pageURL = global.location.href
        window.digitalDataLayer.push(lastpageload)
      }
    }
  */
  useEffect(() => {
    props?.pageviewCallback?.(location?.state?.nopageview)
  })

  function getViewIndex() {
    if (!gridData?.gridFilters.groups?.length) return 0
    const params = currentUrl.searchParams
    const pindex = params.get("group") ?? -1
    return +pindex
  }

  useEffect(() => {
    let pindex = getViewIndex()
    const expectedpindex = readSS("view_mode") ?? 0
    if (pindex < 0) {
      pindex = expectedpindex
      if (!process.browser) return
      const currentUrl = getCurrentUrl()
      currentUrl.search = `group=${pindex}`
      window.history.replaceState({}, "", currentUrl.href)
    } else writeSS("view_mode", pindex)

    setSelected(items[pindex].id)
  }, [location])

  function onGridChanged(e) {
    const currentUrl = getCurrentUrl()
    currentUrl.search = `group=${e?.target?.value}`
    navigation.push(currentUrl.href, { nopageview: false }, { scroll: true })
  }

  return (
    <>
      {gridSSR}
      <Helmet key={gridData?.title ?? Math.random()}>
        <title>{gridData?.title}</title>
        <meta name='description' content={gridData?.description} />
        <meta name='og:title' content={gridData?.ogtitle} />
        <meta name='og:description' content={gridData?.ogdescription} />
      </Helmet>

      <WatchgridProvider>
        <Root
          style={{
            "--is-sub": !ssr && document.querySelector(".header-subnav") ? 1 : 0,
            "--grid-margin-block-start": gridData?.length !== 0 && gridData?.gridFilters?.advanced_filters?.length ? "-44px" : "0px",
          }}
        >
          <PageTitleBlock {...gridData?.gridFilters.heading} />

          {gridData?.gridFilters.groups?.length ? (
            <Wrapper style={{ marginBlockEnd: gridData?.gridFilters?.quick_filters?.length ? "0px" : "40px" }}>
              <List>
                {items.map((v, i) => (
                  <Item key={v.id}>
                    <Input type='radio' id={`grid-radio-${v.id}`} name={"grid-radio"} value={i} onChange={onGridChanged} checked={selected === v.id} />
                    <Label htmlFor={`grid-radio-${v.id}`}>{v.localizedValue}</Label>
                  </Item>
                ))}
              </List>
            </Wrapper>
          ) : null}
          {selected === "f_grid" ? (
            <WatchGrid key={gridData?.title ?? Math.random()} {...gridData} watchgridURL={cloneWatchgridURL} />
          ) : selected === "g_grid" ? (
            <GroupedGrid {...gridData?.gridFilters} grid={grid} fullgridURL={cloneWatchgridURL} gridData={gridData} />
          ) : null}
        </Root>
      </WatchgridProvider>
    </>
  )
}
